import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  TextField,
  Divider,
  Button,
} from "@mui/material";
import {
  getAllTariffs,
  createTariff,
  updateTariff,
  deleteTariff,
  getTariffMetrics,
} from "../../../Restclient/datacollection";
import { InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CustomButton from "../../CommonComponents/CustomButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import CustomActionButton from "../../CommonComponents/CustomActionButton";
import CustomTextField from "../../CommonComponents/CustomTextField";
import CustomDropDown from "../../CommonComponents/CustomDropDown";
import CustomDatePicker from "../../CommonComponents/CustomDatePicker";
import { ThemeContext } from "../../Homepage";
import dayjs from "dayjs";
import CustomDialogBox from "../../CommonComponents/CustomDialogBox";
import CustomToast from "../../CommonComponents/CustomToast";
import "../../../cssfiles/billing.css";
import SearchIcon from "../../../images/LK_Search";

// Validation utility functions
const validateNumeric = (value, fieldName) => {
  if (!value || value.trim() === "") {
    return { isValid: false, message: `${fieldName} is required` };
  }
  const numericRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
  if (!numericRegex.test(value)) {
    return { isValid: false, message: `${fieldName} must be a valid number` };
  }
  const numValue = parseFloat(value);
  if (numValue < 0) {
    return { isValid: false, message: `${fieldName} cannot be negative` };
  }
  return { isValid: true, message: "" };
};

const validatePercentage = (value, fieldName) => {
  if (!value || value.trim() === "") {
    return { isValid: false, message: `${fieldName} is required` };
  }
  const percentRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
  if (!percentRegex.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} must be a valid percentage`,
    };
  }
  const numValue = parseFloat(value);
  if (numValue < 0 || numValue > 100) {
    return {
      isValid: false,
      message: `${fieldName} must be between 0 and 100`,
    };
  }
  return { isValid: true, message: "" };
};

const validatePowerFactor = (value) => {
  if (!value || value.trim() === "") {
    return { isValid: false, message: "Power Factor is required" };
  }
  const numValue = parseFloat(value);
  if (isNaN(numValue) || numValue < 0 || numValue > 1) {
    return { isValid: false, message: "Power Factor must be between 0 and 1" };
  }
  return { isValid: true, message: "" };
};

const validatePositiveInteger = (value, fieldName) => {
  if (!value || value.trim() === "") {
    return { isValid: false, message: `${fieldName} is required` };
  }
  const intRegex = /^[1-9][0-9]*$/;
  if (!intRegex.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} must be a positive integer`,
    };
  }
  return { isValid: true, message: "" };
};

const sanitizeInput = (value, maxLength, allowedPattern) => {
  if (typeof value !== "string") return value;
  let sanitized = value.replace(/<[^>]*>/g, "").trim();
  if (allowedPattern) {
    sanitized = sanitized.replace(allowedPattern, "");
  }
  if (maxLength) {
    sanitized = sanitized.slice(0, maxLength);
  }
  return sanitized;
};

// Per-field character restrictions for Tariff numeric inputs
const TARIFF_FIELD_PATTERNS = {
  fixedCharges: /[^0-9.]/g,
  ratePerUnit: /[^0-9.]/g,
  fac: /[^0-9.]/g,
  electricityDutyPercent: /[^0-9.]/g,
  currentInterest: /[^0-9.]/g,
  ratePerKvah: /[^0-9.]/g,
  minPowerFactor: /[^0-9.]/g,
  powerFactorPenaltyPercent: /[^0-9.]/g,
  powerFactorIncentivePercent: /[^0-9.]/g,
  latePaymentPenaltyPercent: /[^0-9.]/g,
  paymentDueDays: /[^0-9]/g,
  otherCharges: /[^0-9.]/g,
  taxCharges: /[^0-9.]/g,
};

const TariffActions = ({
  tariff,
  setTariffs,
  setTariffInfo,
  setOpenForm,
  setFormMode,
  setDeleteSuccess,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);

  const handleViewClick = () => {
    if (!tariff || !tariff.data || !tariff.data.id) {
      alert("Cannot view tariff - missing data");
      return;
    }

    const tariffToView = {
      id: tariff.data.id,
      data: {
        ...tariff.data,
        fixedCharges: tariff.data.fixedCharges || "",
        ratePerUnit: tariff.data.ratePerUnit || "",
        electricityDutyPercent: tariff.data.electricityDutyPercent || "",
        latePaymentPenaltyPercent: tariff.data.latePaymentPenaltyPercent || "",
        otherCharges: tariff.data.otherCharges || "",
        tariffType: tariff.data.tariffType || "",
        status: tariff.data.status || "",
        effectiveStartDate: tariff.data.effectiveStartDate || "",
        effectiveEndDate: tariff.data.effectiveEndDate || "",
        isKvahBilling: tariff.data.isKvahBilling ?? false,
        ratePerKvah: tariff.data.ratePerKvah || "",
        minPowerFactor: tariff.data.minPowerFactor || "",
        powerFactorPenaltyPercent: tariff.data.powerFactorPenaltyPercent || "",
        powerFactorIncentivePercent:
          tariff.data.powerFactorIncentivePercent || "",
        energySlabs: tariff.data.energySlabs || [
          { unitsFrom: "", unitsTo: "", rate: "" },
        ],
      },
    };

    setTariffInfo(tariffToView);
    setFormMode("view");
    setOpenForm(true);
  };

  const handleUpdateClick = () => {
    if (!tariff || !tariff.data || !tariff.data.name) {
      alert("Cannot update tariff - missing data");
      return;
    }
    const tariffToUpdate = {
      id: tariff.data.id,
      data: {
        ...tariff.data,
        energySlabs: tariff.data.energySlabs || [
          { unitsFrom: "", unitsTo: "", rate: "" },
        ],
      },
    };
    setTariffInfo(tariffToUpdate);
    setFormMode("update");
    setOpenForm(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setOpenConfirm(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setLoading(true);
    deleteTariff(tariff.id, (response) => {
      setLoading(false);
      if (response.success) {
        setTariffs((prev) => prev.filter((t) => t.id !== tariff.id));
        setOpenConfirm(false);
        setDeleteSuccess(1); // Success

        // Auto-close toast after 10 seconds
        setTimeout(() => {
          setDeleteSuccess(0);
        }, 10000);
      } else {
        setOpenConfirm(false);
        setDeleteSuccess(2); // Error

        // Auto-close error toast after 10 seconds
        setTimeout(() => {
          setDeleteSuccess(0);
        }, 10000);
      }
    });
  };

  const handleToggleStatus = () => {
    setLoading(true);
    const newIsActive = !tariff.data.isActive;
    const updateData = {
      ...tariff.data,
      isActive: newIsActive,
      status: newIsActive ? "Active" : "Inactive",
    };

    updateTariff(tariff.data.id, updateData, (response) => {
      setLoading(false);
      if (response.success) {
        setTariffs((prev) =>
          prev.map((t) => {
            if (t.id === tariff.id) {
              return {
                ...t,
                data: {
                  ...t.data,
                  isActive: newIsActive,
                  status: newIsActive ? "Active" : "Inactive",
                },
              };
            }
            return t;
          }),
        );
        setOpenStatusConfirm(false);
      } else {
        alert(response.message || "Error updating tariff status");
      }
    });
  };

  return (
    <>
      <Box className="tariff-actions-container">
        <CustomActionButton buttonText={"View"} onClick={handleViewClick} />
        <CustomActionButton buttonText={"Delete"} onClick={handleDeleteClick} />
        <CustomActionButton buttonText={"Update"} onClick={handleUpdateClick} />
      </Box>

      <CustomDialogBox
        dialogTitle={"Delete Confirmation"}
        dialogText={`Are you sure you want to delete tariff ? This action cannot be undone.`}
        openDialog={openConfirm}
        handleCloseDialog={() => setOpenConfirm(false)}
        handleConfirmDialog={handleDelete}
        confirmText={"Delete"}
        cancelText={"Cancel"}
        type={"Warning"}
        loading={loading}
      />
    </>
  );
};

const TariffMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalTariffs: 0,
    activeTariffs: 0,
    inactiveTariffs: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchMetrics = () => {
      setIsLoading(true);
      getTariffMetrics((response) => {
        if (response.state === true && response.metrics) {
          setMetrics({
            totalTariffs: response.metrics.totalTariffs,
            activeTariffs: response.metrics.activeTariffs,
            inactiveTariffs: response.metrics.inactiveTariffs,
          });
        }
        setIsLoading(false);
      });
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const metricsData = [
    {
      label: "Total Tariffs",
      value: metrics.totalTariffs,
      color: "var(--PrimaryText)",
    },
    {
      label: "Active Tariffs",
      value: metrics.activeTariffs,
      color: "var(--GreenBaseColor)",
    },
    {
      label: "Inactive Tariffs",
      value: metrics.inactiveTariffs,
      color: "var(--GreyBaseColor)",
    },
  ];

  return (
    <div className="tariff-metrics-card card2">
      <div className="tariff-metrics-container">
        {metricsData.map((metric, index) => (
          <React.Fragment key={index}>
            <div className="tariff-metric-item cardtext">
              <div>
                <Typography className="tariff-metric-label">
                  {metric.label}
                </Typography>
              </div>
              <div>
                <Typography
                  className="tariff-metric-value"
                  style={{ color: metric.color }}
                >
                  {metric.value}
                </Typography>
              </div>
            </div>
            {index < metricsData.length - 1 && (
              <Divider
                orientation="vertical"
                variant="middle"
                className="tariff-metric-divider"
                flexItem
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default function UtilityTariffDashboard(props) {
  const themechange = useSelector((state) => state.themedata.theme);
  const [tariffs, setTariffs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [required, setRequired] = useState(0);
  const [validationError, setValidationError] = useState("");
  const [search, setSearch] = useState({ searchText: "", filterSelected: "" });
  const [isSearching, setIsSearching] = useState(false);
  const [searchList, setSearchList] = useState([]);
  const { theme } = useContext(ThemeContext);
  const [formMode, setFormMode] = useState("add");
  const [saveStatus, setSaveStatus] = React.useState(0);
  const [deleteSuccess, setDeleteSuccess] = React.useState(0);

  const initialTariffInfo = {
    id: "",
    data: {
      tariffId: "",
      name: "",
      tariffCategory: "",
      type: "",
      status: "Active",
      isActive: true,
      fixedCharges: "",
      energyCharges: "",
      minimumCharges: "",
      wheelingCharges: "",
      fac: "",
      electricityDutyPercent: "",
      currentInterest: "",
      ratePerUnit: "",
      latePaymentPenaltyPercent: "",
      paymentDueDays: "",
      gridUnitRate: "",
      dgUnitRate: "",
      gridTierStructure: "",
      dgTierStructure: "",
      peakRate: "",
      offPeakRate: "",
      timeOfUsePeriods: "",
      dualMeterRate: "",
      seasonalRate: "",
      effectiveStartDate: "",
      effectiveEndDate: "",
      otherChargesName: "",
      otherCharges: "",
      taxCharges: "",
      isKvahBilling: "No",
      ratePerKvah: "",
      minPowerFactor: "",
      powerFactorPenaltyPercent: "",
      powerFactorIncentivePercent: "",
      todEnabled: "No",
      todApplicableDays: "",
      energyChargeStructure: "",
      energySlabs: [
        { unitsFrom: "", unitsTo: "", rate: "", season: "", timePeriod: "" },
      ],
    },
  };

  const [tariffInfo, setTariffInfo] = useState(initialTariffInfo);

  useEffect(() => {
    const fetchTariffs = () => {
      setIsLoading(true);
      getAllTariffs((response) => {
        getAllCallback(response);
        setIsLoading(false);
      });
    };

    fetchTariffs();
  }, []);

  useEffect(() => {
    if (!openForm) {
      getAllTariffs(getAllCallback);
    }
  }, [openForm]);

  function getAllCallback(response) {
    if (response && response.state === true && response.tariffs) {
      const formattedTariffs = response.tariffs.map((tariff) => ({
        id: tariff.id,
        data: {
          name: tariff.name,
          type: tariff.type,
          fixedCharges: tariff.fixedCharges,
          isActive: tariff.isActive,
          ...tariff,
        },
      }));
      setTariffs(formattedTariffs);
    } else {
      setTariffs([]);
    }
  }

  const refreshTariffs = () => {
    setIsLoading(true);
    getAllTariffs((response) => {
      if (response && response.state === true && response.tariffs) {
        setTariffs(
          response.tariffs.map((tariff) => ({
            id: tariff.id,
            data: {
              name: tariff.name,
              type: tariff.type,
              fixedCharges: tariff.fixedCharges,
              isActive: tariff.isActive,
              ...tariff,
            },
          })),
        );
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (!openForm) {
      refreshTariffs();
    }
  }, [openForm]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshTariffs();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (field) => (event) => {
    const value = event?.target?.value ?? event;

    // Sanitize input with field-specific character restriction
    const sanitizedValue =
      typeof value === "string"
        ? sanitizeInput(value, 100, TARIFF_FIELD_PATTERNS[field] || null)
        : value;

    setTariffInfo((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: sanitizedValue,
      },
    }));
  };

  const validateForm = () => {
    const errors = [];

    // Tariff Name
    if (!tariffInfo.data.name || tariffInfo.data.name.trim() === "") {
      errors.push("Tariff Name is required");
    }

    // Tariff Category
    if (
      !tariffInfo.data.tariffCategory ||
      tariffInfo.data.tariffCategory.trim() === ""
    ) {
      errors.push("Tariff Category is required");
    }

    // Fixed Charges
    const fixedChargesValidation = validateNumeric(
      tariffInfo.data.fixedCharges,
      "Fixed Charges",
    );
    if (!fixedChargesValidation.isValid)
      errors.push(fixedChargesValidation.message);

    // Rate Per Unit
    const ratePerUnitValidation = validateNumeric(
      tariffInfo.data.ratePerUnit,
      "Rate Per Unit",
    );
    if (!ratePerUnitValidation.isValid)
      errors.push(ratePerUnitValidation.message);

    // Energy Charge Structure
    if (
      !tariffInfo.data.energyChargeStructure ||
      tariffInfo.data.energyChargeStructure.trim() === ""
    ) {
      errors.push("Energy Charge Structure is required");
    }

    // FAC
    const facValidation = validateNumeric(tariffInfo.data.fac, "FAC");
    if (!facValidation.isValid) errors.push(facValidation.message);

    // Electricity Duty Percent
    const electricityDutyValidation = validatePercentage(
      tariffInfo.data.electricityDutyPercent,
      "Electricity Duty",
    );
    if (!electricityDutyValidation.isValid)
      errors.push(electricityDutyValidation.message);

    // kVAh Billing fields if enabled
    if (tariffInfo.data.isKvahBilling === "Yes") {
      const ratePerKvahValidation = validateNumeric(
        tariffInfo.data.ratePerKvah,
        "Rate Per kVAh",
      );
      if (!ratePerKvahValidation.isValid)
        errors.push(ratePerKvahValidation.message);
    }

    // Minimum Power Factor (0 < value ≤ 1)
    const minPowerFactorValidation = validatePowerFactor(
      tariffInfo.data.minPowerFactor,
    );
    if (!minPowerFactorValidation.isValid)
      errors.push(`Minimum Power Factor: ${minPowerFactorValidation.message}`);

    // PF Penalty (0–100%)
    const pfPenaltyValidation = validatePercentage(
      tariffInfo.data.powerFactorPenaltyPercent,
      "PF Penalty",
    );
    if (!pfPenaltyValidation.isValid) errors.push(pfPenaltyValidation.message);

    // Late Payment Penalty (0–100%)
    const latePaymentValidation = validatePercentage(
      tariffInfo.data.latePaymentPenaltyPercent,
      "Late Payment Penalty",
    );
    if (!latePaymentValidation.isValid)
      errors.push(latePaymentValidation.message);

    // Payment Due Days (positive integer)
    const paymentDueDaysValidation = validatePositiveInteger(
      tariffInfo.data.paymentDueDays,
      "Payment Due Days",
    );
    if (!paymentDueDaysValidation.isValid)
      errors.push(paymentDueDaysValidation.message);

    // Effective Start Date
    if (!tariffInfo.data.effectiveStartDate) {
      errors.push("Effective Start Date is required");
    }

    // Effective End Date must be after Start Date when both are set
    if (
      tariffInfo.data.effectiveStartDate &&
      tariffInfo.data.effectiveEndDate
    ) {
      if (
        new Date(tariffInfo.data.effectiveEndDate) <=
        new Date(tariffInfo.data.effectiveStartDate)
      ) {
        errors.push("Effective End Date must be after Effective Start Date");
      }
    }

    // Energy Slabs boundary checks
    if (tariffInfo.data.energySlabs && tariffInfo.data.energySlabs.length > 0) {
      tariffInfo.data.energySlabs.forEach((slab, index) => {
        if (slab.unitsFrom && slab.unitsTo) {
          const unitsFrom = parseInt(slab.unitsFrom);
          const unitsTo = parseInt(slab.unitsTo);
          if (unitsFrom >= unitsTo) {
            errors.push(
              `Slab ${index + 1}: Units From must be less than Units To`,
            );
          }
        }
        if (slab.rate && parseFloat(slab.rate) < 0) {
          errors.push(`Slab ${index + 1}: Rate cannot be negative`);
        }
      });
    }

    if (errors.length > 0) {
      setValidationError(errors.join("; "));
      setRequired(1);
      return false;
    }

    return true;
  };

  const handleSaveOrUpdate = async () => {
    try {
      setValidationError("");

      if (!tariffInfo || !tariffInfo.data) {
        setValidationError("Tariff information is missing");
        setRequired(1);
        return;
      }

      // Validate form
      if (!validateForm()) {
        return;
      }

      setIsLoading(true);

      if (tariffInfo.id || tariffInfo.data.id) {
        const tariffId = tariffInfo.data.id || tariffInfo.id;
        const updatePayload = {
          ...tariffInfo.data,
          isActive:
            tariffInfo.data.isActive !== undefined
              ? tariffInfo.data.isActive
              : true,
          status:
            tariffInfo.data.isActive !== undefined
              ? tariffInfo.data.isActive
                ? "Active"
                : "Inactive"
              : tariffInfo.data.status || "Active",
        };
        updateTariff(tariffId, updatePayload, (response) => {
          if (response.success) {
            setTariffs((prev) =>
              prev.map((t) =>
                t.data.id === tariffId
                  ? { id: tariffId, data: response.tariff }
                  : t,
              ),
            );
            setSaveStatus(1);

            setTimeout(() => {
              setOpenForm(false);
              setTariffInfo(initialTariffInfo);
              setFormMode("add");
              setTimeout(() => setSaveStatus(0), 5000);
            }, 5000);
          } else {
            setSaveStatus(2);
            setValidationError(response.error || "Failed to update tariff");
            setRequired(1);
            setTimeout(() => setSaveStatus(0), 5000);
          }
          setIsLoading(false);
        });
      } else {
        const tariffPayload = {
          data: {
            ...tariffInfo.data,
            energySlabs: tariffInfo.data.energySlabs || [],
          },
        };
        createTariff(tariffPayload, (response) => {
          if (response.success) {
            setTariffs((prev) => [...prev, response.tariff]);
            setSaveStatus(1);
            setTimeout(() => {
              setOpenForm(false);
              setTariffInfo(initialTariffInfo);
              setFormMode("add");
              setTimeout(() => setSaveStatus(0), 5000);
            }, 5000);
          } else {
            setSaveStatus(2);
            setValidationError(response.message || "Failed to create tariff");
            setRequired(1);
            setTimeout(() => setSaveStatus(0), 5000);
          }
          setIsLoading(false);
        });
      }
    } catch (error) {
      setValidationError(error.message || "Unknown error occurred");
      setSaveStatus(2);
      setRequired(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchClick = async () => {
    try {
      setIsLoading(true);
      const filteredTariffs = tariffs.filter((tariff) =>
        tariff.data.name
          .toLowerCase()
          .includes(search.searchText.toLowerCase()),
      );
      setSearchList(filteredTariffs);
      setIsSearching(true);
      setIsLoading(false);
    } catch (err) {
      console.error("Search error:", err);
      setIsLoading(false);
    }
  };

  const handleSearchCancel = () => {
    setSearch({ searchText: "", filterSelected: "" });
    setSearchList([]);
    setIsSearching(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearchClick();
    }
  };

  const handleToastClose = () => {
    setSaveStatus(0);
  };

  const handleDeleteToastClose = () => {
    setDeleteSuccess(0);
  };

  const handleAddSlab = () => {
    setTariffInfo((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        energySlabs: [
          ...(prev.data.energySlabs || []),
          { unitsFrom: "", unitsTo: "", rate: "", season: "", timePeriod: "" },
        ],
      },
    }));
  };

  const handleRemoveSlab = (index) => {
    setTariffInfo((prev) => {
      const newSlabs = [...(prev.data.energySlabs || [])];
      newSlabs.splice(index, 1);
      return {
        ...prev,
        data: {
          ...prev.data,
          energySlabs: newSlabs,
        },
      };
    });
  };

  const handleSlabChange = (index, field, valueOrEvent) => {
    const value = valueOrEvent?.target?.value ?? valueOrEvent;

    setTariffInfo((prev) => {
      const newSlabs = [...(prev.data.energySlabs || [])];
      newSlabs[index][field] = value;
      return {
        ...prev,
        data: {
          ...prev.data,
          energySlabs: newSlabs,
        },
      };
    });
  };

  const displayTariffs = isSearching ? searchList : tariffs;
  const isViewMode = formMode === "view";
  const isUpdateMode = formMode === "update" && tariffInfo.id;

  const renderFieldValue = (value) => {
    return (
      <Box className={`field-value field-value--${themechange}`}>
        {value || "-"}
      </Box>
    );
  };

  const renderFormContent = () => (
    <Box
      className={`tariff-form-container tariff-form-container--${themechange}`}
    >
      <Typography
        variant="h5"
        className={`tariff-form-title tariff-form-title--${themechange}`}
      >
        {isViewMode
          ? "View Tariff"
          : tariffInfo.id
            ? "Update Tariff"
            : "Add New Tariff"}
      </Typography>

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Tariff Name
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.name)
              ) : (
                <CustomTextField
                  id={"tariff-name-input"}
                  onChange={handleInputChange("name")}
                  defaultValue={tariffInfo.data.name}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Tariff Category <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.tariffCategory)
              ) : (
                <CustomDropDown
                  onChange={handleInputChange("tariffCategory")}
                  options={[
                    "LT-Domestic",
                    "LT-Commercial",
                    "LT-Industrial",
                    "LT-Agricultural",
                    "HT-Industrial",
                    "HT-Commercial",
                    "EHT-Industrial",
                  ]}
                  defaultValue={tariffInfo.data.tariffCategory}
                  width={224}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Charges
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Fixed Charges
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.fixedCharges)
              ) : (
                <CustomTextField
                  id={"fixed-charges-input"}
                  onChange={handleInputChange("fixedCharges")}
                  defaultValue={tariffInfo.data.fixedCharges}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Rate Per Unit
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.ratePerUnit)
              ) : (
                <CustomTextField
                  id={"rate-per-unit-input"}
                  onChange={handleInputChange("ratePerUnit")}
                  defaultValue={tariffInfo.data.ratePerUnit}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Time of Day (TOD) Enabled
              </Typography>
              {isViewMode ? (
                renderFieldValue(
                  tariffInfo.data.todEnabled === "Yes" ? "Yes" : "No",
                )
              ) : (
                <CustomDropDown
                  onChange={handleInputChange("todEnabled")}
                  options={["Yes", "No"]}
                  defaultValue={tariffInfo.data.todEnabled || "No"}
                  width={224}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                TOD Applicable Days
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.todApplicableDays)
              ) : (
                <CustomDropDown
                  onChange={handleInputChange("todApplicableDays")}
                  options={[
                    "All Days",
                    "Weekdays Only",
                    "Weekends Only",
                    "Monday-Friday",
                    "Saturday-Sunday",
                  ]}
                  defaultValue={tariffInfo.data.todApplicableDays}
                  width={224}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Energy Charges (Slab Structure)
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box className="energy-slabs-container">
              <Box className="energy-slabs-header">
                <Typography className={`energy-slabs-title energy-slabs-title--${themechange}`}>
                  Energy Charges (Slab Structure)
                </Typography>
                {!isViewMode && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddSlab}
                  >
                    Add Slab
                  </Button>
                )}
              </Box>

              <Grid container direction="row" spacing={2}>
                {tariffInfo.data.energySlabs.map((slab, index) => (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={1} className="slab-item">
                      <Grid item xs={12} md={3}>
                        <Typography className="slab-field-label">
                          Units From
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.unitsFrom || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            value={slab.unitsFrom}
                            onChange={(value) =>
                              handleSlabChange(index, "unitsFrom", value)
                            }
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Typography className="slab-field-label">
                          Units To
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.unitsTo || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            value={slab.unitsTo}
                            onChange={(value) =>
                              handleSlabChange(index, "unitsTo", value)
                            }
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography className="slab-field-label">
                          Rate
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.rate || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            value={slab.rate}
                            onChange={(value) =>
                              handleSlabChange(index, "rate", value)
                            }
                          />
                        )}
                      </Grid>

                      {!isViewMode && (
                        <Grid item xs={12} md={2} className="slab-delete-button-container">
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() => handleRemoveSlab(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box> */}

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Energy Charges
        </Typography>

        <Grid container spacing={3} className="form-section">
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Energy Charge Structure{" "}
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.energyChargeStructure)
              ) : (
                <CustomDropDown
                  onChange={handleInputChange("energyChargeStructure")}
                  options={[
                    "Flat Rate",
                    "Slab Structure",
                    "TOD Based",
                    "Seasonal",
                    "TOD + Seasonal",
                  ]}
                  defaultValue={tariffInfo.data.energyChargeStructure}
                  width={224}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box className="energy-slabs-container">
              <Box className="energy-slabs-header">
                <Typography
                  className={`energy-slabs-title energy-slabs-title--${themechange}`}
                >
                  Energy Charges (Slab Structure)
                </Typography>
                {!isViewMode && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddSlab}
                  >
                    Add Slab
                  </Button>
                )}
              </Box>

              <Grid container direction="row" spacing={2}>
                {tariffInfo.data.energySlabs.map((slab, index) => (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={1} className="slab-item">
                      <Grid item xs={12} md={2}>
                        <Typography className="slab-field-label">
                          Units From
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.unitsFrom || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            onChange={(value) =>
                              handleSlabChange(index, "unitsFrom", value)
                            }
                            value={slab.unitsFrom}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography className="slab-field-label">
                          Units To
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.unitsTo || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            value={slab.unitsTo}
                            onChange={(value) =>
                              handleSlabChange(index, "unitsTo", value)
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography className="slab-field-label">
                          Rate (₹/kWh)
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.rate || "-"}</Typography>
                        ) : (
                          <CustomTextField
                            value={slab.rate}
                            onChange={(value) =>
                              handleSlabChange(index, "rate", value)
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography className="slab-field-label">
                          Season
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.season || "-"}</Typography>
                        ) : (
                          <CustomDropDown
                            value={slab.season}
                            onChange={(value) =>
                              handleSlabChange(index, "season", value)
                            }
                            options={[
                              "All Year",
                              "Summer",
                              "Winter",
                              "Monsoon",
                            ]}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography className="slab-field-label">
                          Time Period
                        </Typography>
                        {isViewMode ? (
                          <Typography>{slab.timePeriod || "-"}</Typography>
                        ) : (
                          <CustomDropDown
                            value={slab.timePeriod}
                            onChange={(value) =>
                              handleSlabChange(index, "timePeriod", value)
                            }
                            options={["All Day", "Peak", "Off-Peak", "Normal"]}
                          />
                        )}
                      </Grid>
                      {!isViewMode && (
                        <Grid
                          item
                          xs={12}
                          md={2}
                          className="slab-delete-button-container"
                        >
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() => handleRemoveSlab(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Additional Charges
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                FAC
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.fac)
              ) : (
                <CustomTextField
                  id={"fac-input"}
                  onChange={handleInputChange("fac")}
                  defaultValue={tariffInfo.data.fac}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Electricity Duty (%)
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.electricityDutyPercent)
              ) : (
                <CustomTextField
                  id={"electricity-duty-input"}
                  onChange={handleInputChange("electricityDutyPercent")}
                  defaultValue={tariffInfo.data.electricityDutyPercent}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Current Interest (%)
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.currentInterest)
              ) : (
                <CustomTextField
                  id={"current-interest-input"}
                  onChange={handleInputChange("currentInterest")}
                  defaultValue={tariffInfo.data.currentInterest}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Power Factor Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography
                className={`field-label field-label--narrow field-label--${themechange}`}
              >
                Use kVAh Billing?
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(
                  tariffInfo.data.isKvahBilling === "Yes" ? "Yes" : "No",
                )
              ) : (
                <CustomDropDown
                  id={"kvah-billing-input"}
                  onChange={handleInputChange("isKvahBilling")}
                  options={["Yes", "No"]}
                  defaultValue={tariffInfo.data.isKvahBilling || "No"}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Rate Per kVAh (₹)
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.ratePerKvah)
              ) : (
                <CustomTextField
                  id={"rate-per-kvah-input"}
                  onChange={handleInputChange("ratePerKvah")}
                  defaultValue={tariffInfo.data.ratePerKvah}
                  maxLength={10}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Minimum Power Factor
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.minPowerFactor)
              ) : (
                <CustomTextField
                  id={"min-power-factor-input"}
                  onChange={handleInputChange("minPowerFactor")}
                  defaultValue={tariffInfo.data.minPowerFactor}
                  maxLength={5}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                PF Penalty (%)
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.powerFactorPenaltyPercent)
              ) : (
                <CustomTextField
                  id={"pf-penalty-percent-input"}
                  onChange={handleInputChange("powerFactorPenaltyPercent")}
                  defaultValue={tariffInfo.data.powerFactorPenaltyPercent}
                  maxLength={5}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                PF Incentive (%)
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.powerFactorIncentivePercent)
              ) : (
                <CustomTextField
                  id={"pf-incentive-percent-input"}
                  onChange={handleInputChange("powerFactorIncentivePercent")}
                  defaultValue={tariffInfo.data.powerFactorIncentivePercent}
                  maxLength={5}
                  width={200}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="form-section">
        <Typography variant="h6" className="section-title">
          Payment Terms & Other Charges
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Late Payment Penalty (%)
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.latePaymentPenaltyPercent)
              ) : (
                <CustomTextField
                  id={"late-payment-penalty-input"}
                  onChange={handleInputChange("latePaymentPenaltyPercent")}
                  defaultValue={tariffInfo.data.latePaymentPenaltyPercent}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Payment Due Days
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.paymentDueDays)
              ) : (
                <CustomTextField
                  id={"payment-due-days-input"}
                  onChange={handleInputChange("paymentDueDays")}
                  defaultValue={tariffInfo.data.paymentDueDays}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Effective Start Date
                <span className="required-asterisk">*</span>
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.effectiveStartDate)
              ) : (
                <Box sx={{ overflow: "hidden", maxWidth: 240 }}>
                  <CustomDatePicker
                    name={"effectiveStartDate"}
                    onChange={(date) => {
                      const event = { target: { value: date } };
                      handleInputChange("effectiveStartDate")(event);
                    }}
                    views={["day", "month", "year"]}
                    width={224}
                    defaultValue={
                      tariffInfo.data.effectiveStartDate
                        ? dayjs(tariffInfo.data.effectiveStartDate)
                        : null
                    }
                    placeholder={"DD/MM/YYYY"}
                    disabled={isViewMode}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Effective End Date
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.effectiveEndDate)
              ) : (
                <Box sx={{ overflow: "hidden", maxWidth: 240 }}>
                  <CustomDatePicker
                    name={"effectiveEndDate"}
                    onChange={(date) => {
                      const event = { target: { value: date } };
                      handleInputChange("effectiveEndDate")(event);
                    }}
                    views={["day", "month", "year"]}
                    width={224}
                    defaultValue={
                      tariffInfo.data.effectiveEndDate
                        ? dayjs(tariffInfo.data.effectiveEndDate)
                        : null
                    }
                    placeholder={"DD/MM/YYYY"}
                    disabled={isViewMode}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Other Charges Name
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.otherChargesName)
              ) : (
                <CustomTextField
                  id={"other-charges-name-input"}
                  onChange={handleInputChange("otherChargesName")}
                  defaultValue={tariffInfo.data.otherChargesName}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Other Charges
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.otherCharges)
              ) : (
                <CustomTextField
                  id={"other-charges-input"}
                  onChange={handleInputChange("otherCharges")}
                  defaultValue={tariffInfo.data.otherCharges}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography className={`field-label field-label--${themechange}`}>
                Tax Charges (₹)
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.taxCharges)
              ) : (
                <CustomTextField
                  id={"tax-charges-input"}
                  onChange={handleInputChange("taxCharges")}
                  defaultValue={tariffInfo.data.taxCharges}
                  maxLength={50}
                  width={200}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className="config--form--box">
              <Typography
                className={`field-label field-label--narrow field-label--${themechange}`}
              >
                Status
              </Typography>
              {isViewMode ? (
                renderFieldValue(tariffInfo.data.status)
              ) : (
                <CustomDropDown
                  id={"Status-input"}
                  onChange={handleInputChange("status")}
                  options={["Active", "Inactive"]}
                  defaultValue={tariffInfo.data.status || "Active"}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="form-actions">
        {isViewMode ? (
          <CustomButton
            buttonText={"Close"}
            onClick={() => {
              setOpenForm(false);
              setTariffInfo(initialTariffInfo);
              setFormMode("add");
              setValidationError("");
            }}
          />
        ) : (
          <>
            <CustomDialogBox
              dialogTitle={"Validation Error"}
              dialogText={validationError || "Required fields must be filled"}
              openDialog={required === 1}
              handleCloseDialog={() => setRequired(0)}
              handleConfirmDialog={() => setRequired(0)}
              confirmText={"OK"}
              type={"Warning"}
            />
            <CustomButton
              buttonText={
                tariffInfo.id || tariffInfo.data.tariffId
                  ? "Update Tariff"
                  : "Save Tariff"
              }
              onClick={handleSaveOrUpdate}
              disabled={isLoading}
            />
            <CustomButton
              buttonText={"Cancel"}
              onClick={() => {
                setOpenForm(false);
                setTariffInfo(initialTariffInfo);
                setFormMode("add");
                setValidationError("");
              }}
              disabled={isLoading}
            />
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      className={`tariff-dashboard-container tariff-dashboard-container--${themechange}`}
    >
      {openForm ? (
        renderFormContent()
      ) : (
        <>
          {saveStatus !== 0 && (
            <CustomToast
              openToast={saveStatus !== 0}
              type={saveStatus === 1 ? "Success" : "Error"}
              toastText={
                saveStatus === 1 ? "Tariff Saved Successfully!" : "Save Failed"
              }
              onClose={handleToastClose}
              autoHideDuration={10000}
            />
          )}
          {deleteSuccess !== 0 && (
            <CustomToast
              openToast={deleteSuccess !== 0}
              type={deleteSuccess === 1 ? "Success" : "Error"}
              toastText={
                deleteSuccess === 1
                  ? "Tariff Deleted Successfully!"
                  : "Delete Failed"
              }
              onClose={handleDeleteToastClose}
              autoHideDuration={10000}
            />
          )}
          <TariffMetrics tariffs={tariffs} />

          <Box className="header-container">
            <Box></Box>
            <Box className="search-actions-container">
              <TextField
                size="small"
                placeholder="Search Tariff Name"
                variant="standard"
                value={search.searchText}
                onChange={(e) =>
                  setSearch({ ...search, searchText: e.target.value })
                }
                onKeyPress={handleKeyPress}
                InputProps={{
                  endAdornment: search.searchText ? (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleSearchCancel}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                className={`search-field search-field--${themechange}`}
              />
              <IconButton
                onClick={handleSearchClick}
                style={{
                  fontSize: "12px",
                  width: "30px",
                  minWidth: "30px",
                  fontWeight: 400,
                  color: "var(--Searchtext_muted)",
                  fontFamily: "Roboto",
                  textAlign: "left",
                  justifySelf: "right",
                  cursor: "pointer",
                  textTransform: "none",
                  marginLeft: "0px",
                  padding: 0,
                }}
              >
                <SearchIcon
                  color={theme === "light" ? "#3C3C3C" : "#C1C1C1"}
                  width={30}
                  height={30}
                />
              </IconButton>
              <CustomButton
                buttonText={"+Add Tariff"}
                onClick={() => setOpenForm(true)}
              />
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            className="table-container"
            elevation={0}
          >
            <Table stickyHeader className="customers-table" size="small">
              <TableHead>
                <TableRow className="table-header-row">
                  <TableCell
                    className="table-header-cell"
                    sx={{
                      fontSize:
                        props.widgetprop.props.extended.tablehead.fontsize,
                      fontWeight:
                        props.widgetprop.props.extended.tablehead.fontweight,
                    }}
                  >
                    Tariff Name
                  </TableCell>
                  <TableCell
                    className="table-header-cell"
                    sx={{
                      fontSize:
                        props.widgetprop.props.extended.tablehead.fontsize,
                      fontWeight:
                        props.widgetprop.props.extended.tablehead.fontweight,
                    }}
                  >
                    Fixed Charges
                  </TableCell>
                  <TableCell
                    className="table-header-cell"
                    sx={{
                      fontSize:
                        props.widgetprop.props.extended.tablehead.fontsize,
                      fontWeight:
                        props.widgetprop.props.extended.tablehead.fontweight,
                    }}
                  >
                    Electricity Duty Percent (%)
                  </TableCell>
                  <TableCell
                    className="table-header-cell"
                    sx={{
                      fontSize:
                        props.widgetprop.props.extended.tablehead.fontsize,
                      fontWeight:
                        props.widgetprop.props.extended.tablehead.fontweight,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    className="table-header-cell"
                    sx={{
                      fontSize:
                        props.widgetprop.props.extended.tablehead.fontsize,
                      fontWeight:
                        props.widgetprop.props.extended.tablehead.fontweight,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayTariffs.map((tariff) => (
                  <TableRow key={tariff.id} className="table-body-row">
                    <TableCell
                      className="tariff-table-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {tariff.data.name}
                    </TableCell>
                    <TableCell
                      className="tariff-table-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {tariff.data.fixedCharges}
                    </TableCell>
                    <TableCell
                      className="tariff-table-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {tariff.data.electricityDutyPercent}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className={`table-body-cell tariff-status-cell--${tariff.data.status?.toLowerCase()}`}
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {tariff.data.status}
                    </TableCell>
                    <TableCell
                      className="tariff-table-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      <TariffActions
                        tariff={tariff}
                        setTariffs={setTariffs}
                        setTariffInfo={setTariffInfo}
                        setOpenForm={setOpenForm}
                        theme="light"
                        setFormMode={setFormMode}
                        setDeleteSuccess={setDeleteSuccess}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
